export interface DashboardLeadRow {
  id: string;
  created_at: string;
  source?: string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  status?: string | null;
  budget?: string | null;
  budget_number?: number | null;
  neighborhoods?: string[] | string | null;
  neighborhood_other?: string | null;
  pets?: string | null;
  // Move-in date the renter requested at intake. Stored as TEXT · website form
  // submits ISO YYYY-MM-DD · SMS / other channels may submit free text
  // ("August" · "next month" · etc.). The popover renders this as the row's
  // secondary signal (was time-since-arrival · which was redundant with the
  // bucket title above the rows).
  move_in_date?: string | null;
  // Ch78 · the Website form's SMS checkbox, served by /lead-opportunities.
  // true = asked to be texted; false/null = not asked (NOT a refusal); absent
  // on payloads that predate the field. Read via lib/leadSmsOptIn.ts.
  sms_opted_in?: boolean | null;
  verification_state?: string | null;
  verification_score?: number | null;
  verification_label?: string | null;
  verification_provider?: string | null;
  verification_checked_at?: string | null;
  // Scorer v2: carries intel_tier (ULTRA_REAL..ULTRA_FAKE); absent on rows
  // scored before v2 and on older /lead-opportunities payloads.
  verification_flags?: Record<string, unknown> | null;
  /** The form's email-list tick. Absent on rows that predate it. */
  email_list_opt_in?: boolean | null;
  revenue_opportunity_score?: number | null;
  revenue_opportunity_band?: "high" | "medium" | "low" | null;
  revenue_opportunity_reasons?: string[] | null;
  revenue_opportunity_version?: string | null;
  // PKT-0050 · resolved client-side from the clients fetch via email match
  // (LOWER(TRIM(email))). Replaces the old denormalized leads.assigned_agent_id
  // cache column dropped by migration 033. Null when:
  //   - the lead is unclaimed (status != 'converted')
  //   - the lead has no email (intake gap)
  //   - the lead converted but no matching client row exists (data integrity
  //     edge case · pre-existing)
  // Populated synchronously after Promise.all in fetchDashboardData · ready
  // before setDashboardLeads fires · downstream renders never see an
  // undefined-while-loading state.
  resolved_assigned_agent_id?: string | null;
  // Ch76 · THE unclaimed verdict, stamped by the backend
  // (lead_flow_truth.is_unclaimed) — the same predicate the Admin page's
  // `leads.unclaimed` counts with. `true`/`false` is the verdict; `null` means
  // the backend could not read the claim ledger and refused to guess; absent
  // on payloads that predate the stamp.
  unclaimed?: boolean | null;
}

/**
 * Human-readable move-in date for a lead's `move_in_date` text field. Used
 * by the Incoming Leads popover as the row-level secondary signal — replaces
 * the previously-redundant time-since-arrival (which restated the bucket
 * title above the rows). Move-in date is the operational prioritization
 * signal: closer move-in date = hotter lead.
 *
 * Buckets:
 *   ISO date this year (e.g., "2026-07-31") → "Jul 31"
 *   ISO date other year (e.g., "2027-01-15") → "Jan 15, 2027"
 *   Free text non-empty (e.g., "August", "next month") → as-is (truncated)
 *   Empty/null/whitespace → ""
 */
export function formatLeadMoveInDate(
  rawDate: string | null | undefined,
  now: Date = new Date()
): string {
  if (!rawDate || typeof rawDate !== "string") return "";
  const trimmed = rawDate.trim();
  if (!trimmed) return "";

  // ISO date detection (YYYY-MM-DD with optional T...trailer)
  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const year = Number(isoMatch[1]);
    const month = Number(isoMatch[2]) - 1;
    const day = Number(isoMatch[3]);
    const parsed = new Date(year, month, day);
    if (!Number.isNaN(parsed.getTime())) {
      if (year === now.getFullYear()) {
        return parsed.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
      }
      return parsed.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
  }

  // Free text · cap length so it doesn't break the row layout
  return trimmed.length > 24 ? trimmed.slice(0, 22) + "…" : trimmed;
}

/**
 * Human-readable time-since-arrival for a lead's created_at timestamp.
 * Used by the Incoming Leads popover to anchor daily morning triage:
 * operator sees at a glance which leads are fresh vs aging.
 *
 * Buckets:
 *   <1 min     → "just now"
 *   <60 min    → "Xm ago"
 *   <24 hours  → "Xh ago"
 *   <7 days    → "Xd ago"
 *   else       → locale date "May 14"
 */
export function formatLeadTimeSince(
  createdAt: string,
  now: Date = new Date()
): string {
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return "";
  const diffMs = now.getTime() - created.getTime();
  if (diffMs < 60_000) return "just now";
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return created.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

export interface WeeklyLeadBucket {
  date: string;
  isoDate: string;
  endIsoDate: string;
  count: number;
}

function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

export function getStartOfCurrentWeek(now: Date = new Date()): Date {
  const current = startOfDay(now);
  const offset = (current.getDay() + 6) % 7;
  current.setDate(current.getDate() - offset);
  return current;
}

function formatLocalDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseLocalDateKey(value: string): Date | null {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3])
  );
  return Number.isNaN(date.getTime()) ? null : startOfDay(date);
}

function formatBucketLabel(date: Date, rangeDays: number): string {
  if (rangeDays <= 7) {
    return date.toLocaleDateString("en-US", { weekday: "short" });
  }

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function addDays(date: Date, days: number): Date {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
}

export function buildLeadRangeChart(
  leads: ReadonlyArray<DashboardLeadRow>,
  rangeDays = 7,
  now: Date = new Date()
): WeeklyLeadBucket[] {
  const safeRangeDays = [7, 30, 90].includes(rangeDays) ? rangeDays : 7;
  const start = startOfDay(now);
  start.setDate(start.getDate() - (safeRangeDays - 1));
  const bucketSpanDays = safeRangeDays === 90 ? 7 : 1;
  const bucketCount = Math.ceil(safeRangeDays / bucketSpanDays);

  const buckets = Array.from({ length: bucketCount }, (_, index) => {
    const bucketDate = new Date(start);
    bucketDate.setDate(start.getDate() + index * bucketSpanDays);
    const bucketEndDate = addDays(
      bucketDate,
      Math.min(bucketSpanDays - 1, safeRangeDays - 1 - index * bucketSpanDays)
    );
    const isoDate = formatLocalDateKey(bucketDate);
    const endIsoDate = formatLocalDateKey(bucketEndDate);
    return {
      key: startOfDay(bucketDate).getTime(),
      endKey: startOfDay(bucketEndDate).getTime(),
      isoDate,
      endIsoDate,
      date: formatBucketLabel(bucketDate, safeRangeDays),
      count: 0,
    };
  });

  for (const lead of leads) {
    if (!lead.created_at) continue;
    const leadDate = new Date(lead.created_at);
    if (Number.isNaN(leadDate.getTime())) continue;
    const leadDayKey = startOfDay(leadDate).getTime();
    const bucket = buckets.find(
      (entry) => leadDayKey >= entry.key && leadDayKey <= entry.endKey
    );
    if (bucket) bucket.count += 1;
  }

  return buckets.map(({ date, isoDate, endIsoDate, count }) => ({
    date,
    isoDate,
    endIsoDate,
    count,
  }));
}

export function buildWeeklyLeadChart(
  leads: ReadonlyArray<DashboardLeadRow>,
  now: Date = new Date()
): WeeklyLeadBucket[] {
  return buildLeadRangeChart(leads, 7, now);
}

export function buildLeadDailyChartForRange(
  leads: ReadonlyArray<DashboardLeadRow>,
  startIsoDate: string,
  endIsoDate: string
): WeeklyLeadBucket[] {
  const start = parseLocalDateKey(startIsoDate);
  const end = parseLocalDateKey(endIsoDate);
  if (!start || !end || start.getTime() > end.getTime()) {
    return [];
  }

  const buckets: Array<WeeklyLeadBucket & { key: number }> = [];
  for (
    let bucketDate = new Date(start);
    bucketDate.getTime() <= end.getTime();
    bucketDate = addDays(bucketDate, 1)
  ) {
    const isoDate = formatLocalDateKey(bucketDate);
    buckets.push({
      key: startOfDay(bucketDate).getTime(),
      date: formatBucketLabel(bucketDate, 7),
      isoDate,
      endIsoDate: isoDate,
      count: 0,
    });
  }

  for (const lead of leads) {
    if (!lead.created_at) continue;
    const leadDate = new Date(lead.created_at);
    if (Number.isNaN(leadDate.getTime())) continue;
    const leadDayKey = startOfDay(leadDate).getTime();
    const bucket = buckets.find((entry) => entry.key === leadDayKey);
    if (bucket) bucket.count += 1;
  }

  return buckets.map(({ date, isoDate, endIsoDate, count }) => ({
    date,
    isoDate,
    endIsoDate,
    count,
  }));
}
